import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./PhotoGallery.css";
import photoGalleryBg from "../assets/images/maplines.svg";
import { useTranslation } from "react-i18next";
import { Spin, Alert, Space } from "antd";
import axiosInstance from "../services/axios";
import Heading from "./Heading";
import EmptyState from './EmptyState';

// Advertisement logos (SVGs) - add the SVG assets to `src/assets/images/` with these filenames
import digitalIndia from "../assets/icons/advertisment/Digital India.svg";
import jalYukta from "../assets/icons/advertisment/Jal yukta abhiyan.svg";
import satyameva from "../assets/icons/advertisment/Satyameva.svg";
import myGov from "../assets/icons/advertisment/MyGov.svg";
import panchyatVikas from "../assets/icons/advertisment/Panchyat Vikas.svg";
import panchyatRaj from "../assets/icons/advertisment/Panchyat Raj.svg";
import majhiVasundhara from "../assets/icons/advertisment/Majhi Vasundhara.svg";
import maharashtraShasan from "../assets/icons/advertisment/Maharashtra Shasan.svg";
import sampleVillage from "../assets/images/sampleVillage.png";
import mygramPanchayat from "../assets/icons/advertisment/my_grampanchayath.svg";
import e_village_gov from "../assets/icons/advertisment/e_village_gov.svg";


/* Logos for the advertisement footer/brands */
const advertisementLogoData = [
  { id: 1, imageUrl: majhiVasundhara, link: "https://majhivasundhara.in/mar" },
  { id: 2, imageUrl: maharashtraShasan, link: "https://maharashtra.gov.in/" },
  { id: 3, imageUrl: panchyatRaj, link: "https://panchayat.gov.in/" },
  { id: 4, imageUrl: panchyatVikas, link: "https://majhivasundhara.in/mar" },
  { id: 5, imageUrl: myGov, link: "https://www.mygov.in/" },
  { id: 6, imageUrl: satyameva, link: "https://majhivasundhara.in/mar" },
  { id: 7, imageUrl: jalYukta, link: "https://majhivasundhara.in/mar" },
  { id: 8, imageUrl: digitalIndia, link: "https://www.digitalindia.gov.in/" },
  { id: 9, imageUrl: mygramPanchayat, link: "https://meripanchayat.gov.in/homepage" },
  { id: 10, imageUrl: e_village_gov, link: "https://egramswaraj.gov.in/" },
];

const PhotoGallerySection = ({ data }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleViewAllClick = () => {
    navigate("/photo-gallery");
  };

  return (
    <div
      className="photo-gallery-section section-padding"
      style={{
        backgroundColor: 'var(--color-primary)',
        backgroundImage: `url(${photoGalleryBg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <div className="photo-gallery-header">
        <div className="my-2">
          <Heading title={t("photoGallery.title")} variant="first" />
        </div>
        {data?.length > 8 && <button
          className="photo-gallery-view-button max-sm:hidden"
          onClick={handleViewAllClick}
        >
          {t("photoGallery.viewAllButton")}
        </button>}
      </div>

      {data.length === 0 ? (
        <div style={{ padding: '50px 0' }}>
          <EmptyState description={i18n.language === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />
        </div>
      ) : (
        <Space
          className="photo-gallery-grid"
          size={isMobile ? 12 : 30}
        >
          {(data || []).slice(0, 8).map((item) => (
            <div key={item.id} className="photo-gallery-item" style={{
              padding: 0,
              width: isMobile ? '100%' : '260px',
              maxWidth: '100%'
            }}>
              <img
                src={item.imageUrl}
                alt={t("photoGallery.imageAlt", { id: item.id })}
                className="photo-gallery-image"
                style={{
                  width: '100%',
                  height: isMobile ? '220px' : '176.67px',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </div>
          ))}
        </Space>
      )}

      <button
        className="photo-gallery-view-button max-sm:block hidden"
        onClick={handleViewAllClick}
      >
        {t("photoGallery.viewAllButton")}
      </button>
    </div>
  );
};

const AdvertisimentSection = ({ data: initialData, logoData }) => {
  const { t, i18n } = useTranslation();
  const [ads, setAds] = useState([]);
  const [loadingAds, setLoadingAds] = useState(false);
  const [adsError, setAdsError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
    setLoadingAds(true);
    setAdsError(null);

    axiosInstance
      .get('/advertisements/', { params: { language: languageParam } })
      .then((resp) => {
        if (!isMounted) return;
        let data = resp && resp.data ? resp.data : [];
        if (!Array.isArray(data)) data = data.results || data.data || Object.values(data) || [];

        const mapped = (data || []).map((it) => ({
          id: it.id,
          imageUrl: it.image || it.imageUrl || it.img || '',
          link: it.link || it.url || null,
          title: it.title || '',
        }));

        setAds(mapped);
      })
      .catch((err) => {
        console.error('Failed to load advertisements:', err);
        if (isMounted) {
          setAds([]);
          setAdsError('Failed to load advertisements');
        }
      })
      .finally(() => {
        if (isMounted) setLoadingAds(false);
      });

    return () => { isMounted = false; };
  }, [i18n.language, initialData]);

  return (
    <>
      <div className="advertisement-section section-padding">
        <div className="advertisement-header my-2">
          <Heading
            content={t('advertisement.title')}
          />
        </div>

        {loadingAds ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
        ) : adsError ? (
          <div style={{ padding: 12 }}><Alert type="warning" message={adsError} /></div>
        ) : ads.length === 0 ? (
          <div style={{ padding: '50px 0' }}>
            <EmptyState description={i18n.language === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />
          </div>
        ) : (
          <Space
            className="photo-gallery-grid"
            size={isMobile ? 12 : 30}
          >
            {ads.map((item) => (
              <div key={item.id} className="advertisement-item" style={{
                padding: 0,
                width: isMobile ? '100%' : '260px',
                maxWidth: '100%'
              }}>
                {item.link ? (
                  <a href={item.link} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%' }}>
                    <img
                      src={item.imageUrl}
                      alt={item.title || t('advertisement.imageAlt', { id: item.id })}
                      className="advertisement-image"
                      style={{
                        width: '100%',
                        height: isMobile ? '234px' : '176.67px',
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                  </a>
                ) : (
                  <img
                    src={item.imageUrl}
                    alt={item.title || t('advertisement.imageAlt', { id: item.id })}
                    className="advertisement-image"
                    style={{
                      width: '100%',
                      height: isMobile ? '234px' : '176.67px',
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                )}
              </div>
            ))}
          </Space>
        )}
      </div>
      <div className="advertisement-logos section-padding">
        {logoData.map((item) => (
          <a href={item.link} target="_blank" rel="noreferrer">
            <img
              key={item.id}
              src={item.imageUrl}
              alt={t('advertisement.logoAlt')}
              className="advertisement-logo"
              style={{ width: '90px', height: '90px', objectFit: 'contain', margin: 8 }}
            />
          </a>
        ))}
      </div>
    </>
  );
};

const FullLayout = () => {
  const { i18n } = useTranslation();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
    setLoading(true);
    setError(null);

    axiosInstance
      .get('/gallery/', { params: { language: languageParam } })
      .then((resp) => {
        if (!isMounted) return;
        let data = resp && resp.data ? resp.data : [];
        // normalize envelopes to array
        if (!Array.isArray(data)) data = data.results || data.data || Object.values(data) || [];

        // map to expected shape {id, imageUrl}
        const mapped = (data || []).map((it) => ({
          id: it.id,
          imageUrl: it.image || it.imageUrl || sampleVillage,
          title: it.title || '',
        }));

        setGallery(mapped);
      })
      .catch((err) => {
        console.error('Failed to load gallery:', err);
        if (isMounted) {
          setGallery([]);
          setError('Failed to load gallery');
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [i18n.language]);

  return (
    <div className="full-layout">
      {/* Placeholder for Quick Links Section */}

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
      ) : error ? (
        <div style={{ padding: 12 }}><Alert type="warning" message={error} /></div>
      ) : (
        <PhotoGallerySection data={gallery} />
      )}

      <AdvertisimentSection
        data={[]}
        logoData={advertisementLogoData}
      />

      {/* <OurVillageSection data={villageData} /> */}
    </div>
  );
};

export default FullLayout;
